import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, Loader2, MailCheck, MailX } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { apiCall } from "@/api/client";
import GooglePlayDownloadLink from "@/components/native/GooglePlayDownloadLink";
import NativeAppReturnLink from "@/components/native/NativeAppReturnLink";
import { Button } from "@/components/ui/button";
import { isNativeAuthReturnScheme } from "@/lib/nativeAppLinks";
import { isNativeApp } from "@/lib/runtime";

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState({ state: "loading", message: "Verifierar..." });
  const verificationRequestRef = useRef({ token: null, promise: null });
  const native = isNativeApp();
  const token = searchParams.get("token") || "";
  const returnScheme = searchParams.get("return_scheme") || undefined;
  const hasNativeReturn = isNativeAuthReturnScheme(returnScheme);
  const requestedPurpose = searchParams.get("purpose") === "email_change" ? "email_change" : "register";
  const emailChange = (status.purpose || requestedPurpose) === "email_change";

  useEffect(() => {
    if (!token) {
      setStatus({ state: "error", message: "Verifieringslänken saknar token." });
      return;
    }

    if (verificationRequestRef.current.token !== token) {
      verificationRequestRef.current = {
        token,
        promise: apiCall("/api/auth/verify-email", {
          method: "POST",
          body: JSON.stringify({ token }),
        }),
      };
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await verificationRequestRef.current.promise;
        if (!cancelled) {
          setStatus({
            state: "success",
            purpose: response?.purpose || requestedPurpose,
            message: response?.message || "E-postadressen är verifierad. Du kan nu logga in.",
          });
        }
      } catch (error) {
        if (!cancelled) {
          setStatus({
            state: "error",
            purpose: requestedPurpose,
            message: error?.message || "Verifieringen misslyckades. Länken kan vara ogiltig eller förbrukad.",
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [requestedPurpose, token]);

  const content = (
    <>
      {status.state === "loading" ? (
        <div className="native-v3-verify-state" data-state="loading">
          <Loader2 className="animate-spin" aria-hidden="true" />
          <p>{status.message}</p>
        </div>
      ) : null}
      {status.state === "success" ? (
        <div className="native-v3-verify-state" data-state="success">
          <MailCheck aria-hidden="true" />
          <h2>{emailChange ? "Din e-postadress är uppdaterad" : "E-postadressen är verifierad"}</h2>
          <p>{status.message}</p>
          <NativeAppReturnLink result="email-verified" returnScheme={returnScheme}>
            {emailChange ? "Öppna Lapstr" : "Öppna Lapstr och logga in"}
          </NativeAppReturnLink>
          {!emailChange && !hasNativeReturn ? <GooglePlayDownloadLink /> : null}
        </div>
      ) : null}
      {status.state === "error" ? (
        <div className="native-v3-verify-state" data-state="error">
          <MailX aria-hidden="true" />
          <h2>Verifieringen misslyckades</h2>
          <p>{status.message}</p>
          <NativeAppReturnLink result="email-verification-failed" returnScheme={returnScheme}>
            Tillbaka till Lapstr-appen
          </NativeAppReturnLink>
        </div>
      ) : null}
      {status.state !== "loading" && !hasNativeReturn ? (
        <Button onClick={() => navigate("/", { replace: true })} className="w-full">
          Fortsätt till Lapstr
        </Button>
      ) : null}
    </>
  );

  return (
    <div className={`native-v3-public-flow${native ? "" : " public-auth-v3"}`}>
      <header className="native-v3-public-flow-header">
        <Link to="/auth" aria-label="Tillbaka till inloggning" className="native-v3-public-back">
          <ArrowLeft aria-hidden="true" />
        </Link>
        <div className="native-v3-public-brand">
          <img src="/icons/icon-512x512.png" alt="" aria-hidden="true" />
          <span>Lapstr</span>
        </div>
      </header>
      <main className="native-v3-public-flow-main">
        <div className="native-v3-public-flow-intro">
          <p className="native-v3-auth-kicker">E-POSTVERIFIERING</p>
          <h1 className="native-v3-auth-title">
            {emailChange ? "Bekräfta din nya e-post" : "Bekräfta din e-post"}
          </h1>
        </div>
        <div className="native-v3-verify-content">{content}</div>
      </main>
    </div>
  );
}
